/**
 * Google Tag Manager plugin for Nuxt
 * Initializes GTM and tracks route changes in SPA mode using vue-gtm
 *
 * Configuration:
 * - GTM Container ID: (Veloair)
 * - GTM Account ID: (Caravelo)
 *
 * Environment Setup:
 * - Get gtm_auth from GTM Admin > Environments > env-4
 * - Set GTM_AUTH and GTM_PREVIEW in .env files
 *
 * Usage in components:
 *   const { $gtm } = useNuxtApp()
 *   $gtm.trackEvent({ event: 'custom_event', ... })
 */

import { createGtm, type GtmQueryParams } from '@gtm-support/vue-gtm';
import type { Router } from 'vue-router';

export default defineNuxtPlugin({
  name: 'gtm',
  setup(nuxtApp) {
    const router = useRouter();
    const config = useRuntimeConfig();
    const gtmConfig = config.public.gtm;

    function gtmDebug(string: string, type?: 'debug' | 'info' | 'warn' | 'error') {
      if (gtmConfig.debug) {
        console[type ?? 'debug']('[GTM]: ', string);
      }
    }

    // Skip if GTM is disabled or ID not configured
    if (!gtmConfig.enabled || !gtmConfig.id) {
      gtmDebug('GTM is disabled or GTM_ID not configured, skipping initialization', 'warn');
      return;
    }

    gtmDebug(`Initializing with container ID: ${gtmConfig.id}`, 'info');

    // Build query params only if auth is provided (for custom environments)
    let queryParams: GtmQueryParams | undefined;
    if (gtmConfig.queryParams.gtm_auth) {
      queryParams = {
        gtm_auth: gtmConfig.queryParams.gtm_auth,
        gtm_preview: gtmConfig.queryParams.gtm_preview || 'env-4',
        gtm_cookies_win: gtmConfig.queryParams.gtm_cookies_win || 'x',
      };
      gtmDebug(`Using custom environment: ${queryParams.gtm_preview}`, 'info');
    }

    // Initialize GTM using vue-gtm
    const gtmInstance = createGtm({
      id: gtmConfig.id,
      queryParams,
      defer: false,
      compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
      enabled: gtmConfig.enabled,
      debug: gtmConfig.debug,
      loadScript: true,
      vueRouter: router as Router,
    });

    // Install GTM plugin
    nuxtApp.vueApp.use(gtmInstance);
  },
});